import { cn } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { BadgeAlert, BadgeCheck } from "lucide-react";
import ProfileRadarChart from "@/components/blocks/common/ProfileRadarChart";
import { formatDate, formatTrade } from "@/helpers/helperFunctions";
import { useRouter } from "next/router";
import clsx from "clsx";
import { FaAngleRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useAppDetails } from "@/contexts/AppDetailsContext";

// ========== Types ==========
interface TraderMetaData {
  win_p?: number;
  total_trades?: number;
  total_strategies?: number;
  copiers_count?: number;
  user_meta?: {
    badge?: string;
  };
  trading_profile_categories?: string[];
  trading_profile_graph?: Record<string, number>;
}

interface TraderUser {
  first_name?: string;
  last_name?: string;
  created_at?: string;
  unique_key: string;
}

export interface TraderCardItem {
  user: TraderUser;
  meta_data: TraderMetaData;
}

interface TraderCardProps {
  item: TraderCardItem;
  animate?: boolean;
}

function getNameInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w.charAt(0))
    .join("")
    .toUpperCase();
}

function getBadgeIcon(value?: string) {
  if (!value) return null;

  const common = "w-[16px] h-[16px] ml-2";

  const badgeStyles: Record<string, JSX.Element> = {
    rock: <BadgeAlert className={clsx("text-[#9B0505]", common)} />,
    bronze: <BadgeCheck className={clsx("text-[#cd7f32]", common)} />,
    silver: <BadgeCheck className={clsx("text-[#19438A]", common)} />,
    gold: <BadgeCheck className={clsx("text-[#FFD700]", common)} />,
  };

  return badgeStyles[value] ?? null;
}

function getRadarChartData(
  categories: string[] = [],
  profileData: Record<string, number> = {},
) {
  const base = Object.entries(profileData).map(([name, value]) => ({
    name,
    value,
  }));

  const missing = categories
    .filter((c) => !base.some((d) => d.name === c))
    .map((c) => ({ name: c, value: 0 }));

  return [...base, ...missing].sort((a, b) => a.name.localeCompare(b.name));
}

// ========== Component ==========
export default function TraderCard({ item, animate }: TraderCardProps) {
  const router = useRouter();
  const { details } = useAppDetails();
  const firstName = item?.user?.first_name?.split(" ")[0] ?? "";
  const lastName = item?.user?.last_name?.split(" ")[0] ?? "";
  const fullName = `${firstName} ${lastName}`.trim();
  const { t } = useTranslation("build-page");
  let badgeValue: string | undefined = undefined;

  try {
    const badgeObj = JSON.parse(item?.meta_data?.user_meta?.badge ?? "{}");
    badgeValue = badgeObj?.last_badge;
  } catch {}

  const categories = item?.meta_data?.trading_profile_categories ?? [];
  const profileGraph = item?.meta_data?.trading_profile_graph ?? {};

  const radarData = getRadarChartData(categories, profileGraph);

  return (
    <Card
      className={cn(
        "p-2 w-full  mb-2 pb-3 pt-2",
        animate &&
          "transition-all duration-500 hover:bg-blue-900/[0.08] hover:border-blue-400/20 hover:shadow-[0_20px_40px_rgba(0,0,0,0.4)] hover:-translate-y-1 antialiased",
      )}
    >
      {/* Header */}
      <div
        className={cn(
          "flex items-center w-fulls text-sm justify-between",
          !details.isWhiteLabled && "cursor-pointer",
        )}
        onClick={() => {
          router.push(
            `/profile/${item.user.unique_key}?locale=${
              router.query.locale || "en"
            }`,
          );
        }}
      >
        <div className="flex items-center w-fit cursor-pointer text-sm ">
          {/* Avatar initials */}
          <div
            className={cn(
              "flex items-center justify-center rounded-full bg-muted w-[28px] h-[28px] mr-2 text-xs",
            )}
          >
            {getNameInitials(fullName)}
          </div>

          {fullName}

          {/* Badge */}
          {getBadgeIcon(badgeValue)}
        </div>

        {
          <div className="flex text-sm hover:underline text-[var(--accent-primary)] font-medium gap-2 items-center cursor-pointer">
            <p>{t("View")}</p>
            <FaAngleRight />
          </div>
        }
      </div>

      {/* Member Since */}
      <div className="text-[8px] text-muted-foreground mt-[4px]">
        Member since {formatDate(item?.user?.created_at ?? "-", "DD-MMM-YY")}
      </div>

      {/* Radar Chart + Stats */}
      <div className="sm:flex sm:justify-between mt-2">
        <div
          className="w-full sm:w-[49%] 2xl:w-[55%] flex items-center justify-center "
          id="section1-chart"
        >
          <ProfileRadarChart data={radarData}></ProfileRadarChart>
        </div>

        {/* Stats Section */}
        <div className="w-full sm:w-[49%] 2xl:w-[45%] flex sm:flex-col justify-center gap-8 md:gap-2 flex-row sm:justify-between ">
          {/* Win + Copiers */}
          <div className="flex justify-around gap-8 md:gap-2 items-end">
            <div>
              <div className="text-2xl font-semibold leading-none">
                {typeof item?.meta_data?.win_p === "number" ? (
                  <span
                    className={
                      item.meta_data.win_p >= 75
                        ? "text-[--marketplace-green]"
                        : item.meta_data.win_p >= 50
                          ? "text-[--marketplace-green]"
                          : "text-[--marketplace-red]"
                    }
                  >
                    {item.meta_data.win_p.toFixed(0)}
                    <span className="text-xs ml-[1px]">%</span>
                  </span>
                ) : (
                  "NA"
                )}
              </div>
              <div className="text-[10px] text-[var(--marketplace-grey)] font-medium">
                Average <br />
                Win
              </div>
            </div>

            <div>
              <div className="text-lg font-medium leading-none">
                {formatTrade(item?.meta_data?.copiers_count ?? 0)}
              </div>
              <div className="text-[10px] text-[var(--marketplace-grey)] font-medium">
                Copiers <br /> Count
              </div>
            </div>
          </div>

          {/* Total Stats */}
          <div className="flex justify-around gap-8 md:gap-2 items-end ">
            <div>
              <div className="text-[10px] text-[var(--marketplace-grey)] font-medium">
                Total
                <br /> Strategies
              </div>
              <div className="text-lg font-medium leading-none">
                {formatTrade(item?.meta_data?.total_strategies ?? 0)}
              </div>
            </div>

            <div>
              <div className="text-[10px] text-[var(--marketplace-grey)] font-medium">
                Total <br />
                Trades
              </div>
              <div className="text-lg font-medium leading-none">
                {formatTrade(item?.meta_data?.total_trades ?? 0)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
